import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { Invoice, Payment } from "@/lib/types";
import { money, shortDate, titleCase } from "@/lib/format";
import { Card, Loading, Empty, Tag, Button, Modal, Field, toast } from "@/components/ui";
import { useMe } from "@/app/MeContext";

interface Summary { invoices: Invoice[]; payments: Payment[]; buildTotal: number; buildPaid: number; licenseMonthly: number | null; nextInvoice: string | null; openBalance: number }

export function BillingPanel({ accountId }: { accountId: string }) {
  const me = useMe();
  const { data, loading, refetch } = useQuery<Summary>(`billing:${accountId}`, () => api.get<Summary>(`/billing/accounts/${accountId}/summary`), [accountId]);
  const [creating, setCreating] = useState(false);
  const poll = async () => { try { const r = await api.post<{ updated: number; payments: number }>("/billing/poll", {}); toast(`Wave: ${r.updated} invoices updated, ${r.payments} payments`); refetch(); invalidate(`account:${accountId}`); } catch (e) { toast((e as Error).message, true); } };
  return (
    <>
      <Card title="Billing" actions={<><span className="xs muted">FROM WAVE</span>{me.role === "admin" && <><Button size="sm" variant="ghost" onClick={poll}>Sync</Button><Button size="sm" onClick={() => setCreating(true)}>New invoice</Button></>}</>}>
        {loading && !data ? <Loading /> : !data ? null : (
          <>
            <div className="card-body">
              <dl className="kv">
                <dt>Build</dt><dd className="mono">{data.buildTotal ? `${money(data.buildTotal)} · ${data.buildPaid >= data.buildTotal ? "paid" : `${money(data.buildPaid)} paid`}` : <span className="muted" style={{ fontFamily: "var(--font)" }}>—</span>}</dd>
                <dt>License</dt><dd className="mono">{data.licenseMonthly ? `${money(data.licenseMonthly)}/mo` : <span className="muted" style={{ fontFamily: "var(--font)" }}>—</span>}</dd>
                <dt>Next invoice</dt><dd>{data.nextInvoice ? shortDate(data.nextInvoice) : <span className="muted">—</span>}</dd>
                <dt>Open balance</dt><dd className="mono" style={data.openBalance > 0 ? { color: "var(--text)" } : undefined}>{money(data.openBalance, { cents: true })}</dd>
              </dl>
            </div>
            {data.invoices.length === 0 ? <Empty>No invoices yet.</Empty> : (
              <table className="table">
                <thead><tr><th>Invoice</th><th>Kind</th><th className="right">Total</th><th>Status</th><th>Due</th></tr></thead>
                <tbody>
                  {data.invoices.map((i) => (
                    <tr key={i.id}>
                      <td className="num">{i.viewUrl ? <a href={i.viewUrl} target="_blank" rel="noreferrer">{i.waveNumber}</a> : i.waveNumber}</td>
                      <td className="muted-2 small">{titleCase(i.kind)}</td>
                      <td className="num right">{money(i.total, { cents: true })}</td>
                      <td>{i.status === "overdue" ? <Tag color="outline">Overdue</Tag> : <Tag color={i.status === "paid" ? "green" : "neutral"}>{titleCase(i.status)}</Tag>}</td>
                      <td className="muted small">{i.status === "paid" ? `Paid ${shortDate(i.paidAt)}` : shortDate(i.dueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Card>
      {creating && <NewInvoiceModal accountId={accountId} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); refetch(); }} />}
    </>
  );
}

function NewInvoiceModal({ accountId, onClose, onCreated }: { accountId: string; onClose: () => void; onCreated: () => void }) {
  const [f, setF] = useState({ kind: "other", description: "", amount: "", memo: "", send: true });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post("/billing/invoices", { accountId, kind: f.kind, items: [{ description: f.description, quantity: 1, unitPrice: Number(f.amount) }], memo: f.memo || undefined, send: f.send }); toast("Invoice created in Wave"); onCreated(); } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  return (
    <Modal title="New Wave invoice" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !f.description || !f.amount} onClick={save}>Create{f.send ? " and send" : ""}</Button></>}>
      <div className="form-grid">
        <Field label="Kind"><select className="select" value={f.kind} onChange={(e) => setF((x) => ({ ...x, kind: e.target.value }))}><option value="build_deposit">Build deposit</option><option value="build_balance">Build balance</option><option value="license">License</option><option value="other">Other</option></select></Field>
        <Field label="Amount"><input className="input" type="number" step="0.01" value={f.amount} onChange={(e) => setF((x) => ({ ...x, amount: e.target.value }))} /></Field>
      </div>
      <Field label="Line description"><input className="input" value={f.description} onChange={(e) => setF((x) => ({ ...x, description: e.target.value }))} /></Field>
      <Field label="Memo"><input className="input" value={f.memo} onChange={(e) => setF((x) => ({ ...x, memo: e.target.value }))} /></Field>
      <label className="row gap-8"><input type="checkbox" checked={f.send} onChange={(e) => setF((x) => ({ ...x, send: e.target.checked }))} /> Approve and send from Wave to the billing contact</label>
    </Modal>
  );
}
